import { Stack } from '@vtex/admin-ui'
import { useDrawerSubForm } from 'hooks'
import { useCallback, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  SaveButton,
  UseDrawerStateReturn,
} from 'ui'
import { create } from 'vest'
import {
  ProgressiveDiscount,
  ProgressiveDiscountConfig,
  ProgressiveDiscountEffect,
  ProgressiveDiscountTrigger,
} from '../../../../../typings'
import { SCOPE_OPTIONS } from '../../../../ItemsView/typing'
import { EditorBenefit } from './Benefit'
import { validationSuite as benefitValidationSuite } from './Benefit/validation'
import messages from './messages'
import { EditorTrigger } from './Trigger'
import { validationSuite as triggerValidationSuite } from './Trigger/validation'
import { isValid } from '../../../../utils'

type Props = {
  initialConfig: ProgressiveDiscountEffect
  onSave: (config: ProgressiveDiscountEffect) => void
  state: UseDrawerStateReturn
}

type ProgressiveDiscountEffectEditor = {
  trigger: ProgressiveDiscountTrigger
  benefit: Array<Nullable<ProgressiveDiscountConfig>>
}

const EMPTY_EFFECT: ProgressiveDiscountEffectEditor = Object.freeze({
  trigger: {
    [SCOPE_OPTIONS.SKUS]: [],
    [SCOPE_OPTIONS.COLLECTIONS]: [],
    sameItemsOnly: false,
  },
  benefit: [{ quantity: undefined, discount: undefined }],
})

export function EditorProgressiveDiscount(props: Props) {
  const { state, initialConfig, onSave } = props
  const { hide, open } = state

  const isFilled = useCallback(
    (
      config: ProgressiveDiscountEffect
    ): config is NonNullableObject<ProgressiveDiscountEffect> =>
      !!config.trigger && !!config.benefit,
    []
  )

  const config = useMemo(
    () => (isFilled(initialConfig) ? initialConfig : { ...EMPTY_EFFECT }),
    [initialConfig, isFilled]
  )

  const intl = useIntl()

  const validation = create(
    ({ trigger, benefit }: NonNullableObject<ProgressiveDiscountEffect>) => {
      triggerValidationSuite(intl)(trigger)
      benefitValidationSuite(intl)(benefit)
    }
  )

  const { drawerForm } = useDrawerSubForm<ProgressiveDiscountEffectEditor>({
    config,
    isOpen: open,
    validationFn: validation,
  })

  const handleSave = async () => {
    const valid = await drawerForm.trigger()
    const values = drawerForm.getValues()

    if (!isValid<NonNullableObject<ProgressiveDiscountEffect>>(values, valid))
      return

    values.benefit.sort((a, b) => a.quantity - b.quantity)

    onSave(values)
    hide()
  }

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>
          <FormattedMessage {...messages.title} />
        </DrawerTitle>
        <DismissButton onHide={hide} />
      </DrawerHeader>
      <DrawerContent>
        <Stack space="$space-10" fluid>
          <EditorTrigger name={ProgressiveDiscount.Trigger} form={drawerForm} />
          <EditorBenefit name={ProgressiveDiscount.Benefit} form={drawerForm} />
        </Stack>
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
